import { DataFilterExtension } from "@deck.gl/extensions";
import { PathLayer, ScatterplotLayer, SolidPolygonLayer, TextLayer } from "@deck.gl/layers";
import { MapboxOverlay } from "@deck.gl/mapbox";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { activeRoots } from "../lib/active";
import { GROUP_RGB, groupOfKind, KIND_LABEL } from "../lib/colors";
import { dataUrl, type GedYear, type NamedEvents } from "../lib/data";
import { fmtDeaths } from "../lib/format";
import { FxLayer } from "../fx/FxLayer";
import { CinemaFilm } from "../panels/Cinema";
import { cinematicPitch, gradeOf } from "../fx/logic";
import { motionFx, useFx } from "../fx/state";
import { zoomForWidth } from "../lib/replay";
import { useApp, visibleRange, type PickedEvent } from "../lib/store";
import { formatDate, MONTHS, tToYmd, yearLabel, type DatePoint } from "../lib/time";
import { addBorderLayers, epochIndex, epochTime, setBordersVisible, setBorderTime } from "./borders";
import {
  buildAreas, buildGed, buildGrid, buildNamed, DETAIL_SPAN, fadeWindow, useAreas, useGed, useNamedEvents, type AreaPoly,
  type PointSet,
} from "./useMapData";

const EMPTY = new Float32Array(0);
let protocolAdded = false;

function eventDateLabel(t0: number, t1: number, prec: number, julian: boolean): string {
  const [y, m, d] = tToYmd(t0);
  const a: DatePoint = { y, m, d, prec, julian };
  const one = formatDate(a);
  if (prec < 11 || t1 - t0 <= 1.5 / 365) return one;
  const [y2, m2, d2] = tToYmd(t1 - 1 / 365);
  if (julian) return `${one} – ${formatDate({ y: y2, m: m2, d: d2, prec, julian })}`;
  if (y2 === y && m2 === m) return `${d} – ${d2} ${MONTHS[m - 1]} ${yearLabel(y)}`;
  return `${one} – ${formatDate({ y: y2, m: m2, d: d2, prec, julian })}`;
}

export function MapView() {
  const el = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const overlay = useRef<MapboxOverlay | null>(null);
  const [styleReady, setStyleReady] = useState(false);

  const meta = useApp((s) => s.meta);
  const conflicts = useApp((s) => s.conflicts);
  const t = useApp((s) => s.t);
  const span = useApp((s) => s.span);
  const selected = useApp((s) => s.selected);
  const focus = useApp((s) => s.focus);
  const hoverConflict = useApp((s) => s.hoverConflict);
  const layers = useApp((s) => s.layers);
  const kinds = useApp((s) => s.kinds);
  const event = useApp((s) => s.event);
  const replaying = useApp((s) => !!s.replay);
  const cinematic = useFx((s) => motionFx(s));
  const [a, b] = visibleRange(t, span);

  // --- map setup ---------------------------------------------------------------------------
  useEffect(() => {
    if (!el.current || mapRef.current) return;
    if (!protocolAdded) {
      maplibregl.addProtocol("pmtiles", new Protocol().tile);
      protocolAdded = true;
    }
    const origin = window.location.origin;
    const v = useApp.getState().view;
    const map = new maplibregl.Map({
      container: el.current,
      style: { version: 8, sources: {}, layers: [{ id: "bg", type: "background", paint: { "background-color": "#0b1016" } }] },
      center: [v.lon, v.lat],
      zoom: v.zoom,
      minZoom: 0.6,
      maxZoom: 12,
      renderWorldCopies: true,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.AttributionControl({
      compact: true,
      customAttribution: '<a href="https://www.naturalearthdata.com/" target="_blank" rel="noopener">Natural Earth</a> · borders <a href="https://www.openhistoricalmap.org/" target="_blank" rel="noopener">OpenHistoricalMap</a> (CC0), <a href="https://github.com/aourednik/historical-basemaps" target="_blank" rel="noopener">historical-basemaps</a> (GPL-3.0), <a href="https://commons.wikimedia.org/wiki/File:Divisions_of_Lithuania_1385.svg" target="_blank" rel="noopener">HetmanTheResearcher</a> (CC BY 4.0) · data UCDP, COW, Wikidata, Wikipedia',
    }), "bottom-right");
    mapRef.current = map;
    if (import.meta.env.DEV) (window as unknown as { __map: maplibregl.Map }).__map = map;
    fetch(dataUrl("basemap/style.json")).then((r) => r.json()).then((style) => {
      const s = JSON.parse(JSON.stringify(style).replaceAll("__ORIGIN__", origin));
      map.setStyle(s);
      map.once("styledata", () => setStyleReady(true));
    }).catch((e) => useApp.getState().setError(`Basemap failed to load: ${e}`));

    const dbg = import.meta.env.DEV ? new URLSearchParams(window.location.search).get("debug") ?? "" : "";
    if (!dbg.includes("nodeck")) {
      // Overlaid (own canvas), not interleaved: sharing MapLibre's WebGL context stalls frame production under
      // software rendering (seen with SwiftShader), which some real browsers also fall back to.
      const ov = new MapboxOverlay({ interleaved: dbg.includes("interleaved"), layers: [] });
      map.addControl(ov as unknown as maplibregl.IControl);
      overlay.current = ov;
      if (import.meta.env.DEV) (window as unknown as { __deck: MapboxOverlay }).__deck = ov;
    }
    map.on("moveend", (e: { fxDrift?: boolean }) => {
      if (e.fxDrift) return; // the replay camera's slow drift
      const c = map.getCenter();
      useApp.getState().setView({ lon: c.lng, lat: c.lat, zoom: map.getZoom() });
    });
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // fly-to commands
  useEffect(() => useApp.subscribe((s, p) => {
    const map = mapRef.current;
    if (!map || !s.flyTo || s.flyTo === p.flyTo) return;
    const f = s.flyTo;
    const mobile = window.innerWidth < 900;
    // during a replay the caption card covers the top of the map and, on phones, the collapsed sheet the bottom
    // (the card may not be rendered yet when the replay's first move starts: assume its usual height)
    const hud = s.replay ? document.querySelector<HTMLElement>(".replay-hud") : null;
    const hudBottom = hud ? hud.offsetTop + hud.offsetHeight : mobile ? 140 : 160;
    const padding = s.replay ? { top: hudBottom + 12, bottom: mobile ? 100 : 40, left: 40, right: 40 }
      : mobile ? { top: 70, bottom: Math.round(window.innerHeight * 0.45), left: 30, right: 30 } : { top: 80, bottom: 60, left: 60, right: 60 };
    // replays in the cinematic mode: a tilted camera, turned a little differently for each move
    const tilt = s.replay && motionFx(useFx.getState());
    const turn = tilt ? ((f.id * 7919) % 21) - 10 : 0;
    if (f.bbox) {
      const [w, s0, e, n] = f.bbox;
      if (tilt) {
        const cam = map.cameraForBounds([[w, s0], [e, n]], { padding, maxZoom: 6.5 });
        if (cam?.center && cam.zoom != null) {
          map.flyTo({ center: cam.center, zoom: cam.zoom, pitch: cinematicPitch(cam.zoom), bearing: turn, duration: f.duration ?? 1300 });
          return;
        }
      }
      map.fitBounds([[w, s0], [e, n]], { padding, maxZoom: 6.5, duration: f.duration ?? 1300 });
    } else if (f.lon != null && f.lat != null) {
      const zoom = f.exact ? zoomForWidth(f.zoom ?? 4, map.getContainer().clientWidth) : Math.max(map.getZoom(), f.zoom ?? 4);
      map.flyTo({
        center: [f.lon, f.lat], zoom, duration: f.duration ?? 1300, ...(s.replay ? { padding } : {}),
        ...(tilt ? { pitch: cinematicPitch(zoom), bearing: turn } : {}),
      });
    }
  }), []);

  // the camera levels out when a replay ends (or the cinematic mode is turned off)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || (replaying && cinematic)) return;
    if (map.getPitch() > 0.5 || Math.abs(map.getBearing()) > 0.5) map.easeTo({ pitch: 0, bearing: 0, duration: 1100 });
  }, [replaying, cinematic]);

  // historical borders: added once the style and the data version are known; refiltered when the playhead crosses
  // a date at which borders or place names change (the tileset holds every era)
  const [bordersReady, setBordersReady] = useState(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady || !meta || bordersReady) return;
    const before = map.getLayer("ocean") ? "ocean" : "lake";
    addBorderLayers(map, `pmtiles://${window.location.origin}/data/${meta.borders.tiles}?v=${meta.version}`, before,
      "place-label", epochTime(meta.borders.changes, useApp.getState().t));
    setBordersReady(true);
  }, [styleReady, meta, bordersReady]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !meta || !bordersReady) return;
    // At most one refilter every 0.12 s while the playhead keeps moving (playing, scrolling or dragging the timeline),
    // then one for where it stops: each refilter re-reads the visible tiles. (Cancelling the pending refilter at every
    // crossing froze the borders for as long as the timeline was being scrolled: wheel and drag input come once a
    // frame, and at most zoom levels nearly every frame crosses a change.)
    const borders = meta.borders.changes, places = meta.place_changes ?? [];
    const key = (t: number) => `${epochIndex(borders, t)}:${epochIndex(places, t)}`;
    let shown = "", last = -1e9, raf = 0, timer = 0;
    const run = () => {
      raf = 0;
      last = performance.now();
      const now = useApp.getState().t;
      shown = key(now);
      setBorderTime(map, epochTime(borders, now), epochTime(places, now));
    };
    const check = () => {
      if (raf || timer || key(useApp.getState().t) === shown) return;
      const wait = last + 120 - performance.now();
      if (wait > 0) timer = window.setTimeout(() => { timer = 0; raf = requestAnimationFrame(run); }, wait);
      else raf = requestAnimationFrame(run);
    };
    check();
    const unsubscribe = useApp.subscribe((s, p) => { if (s.t !== p.t) check(); });
    return () => { unsubscribe(); cancelAnimationFrame(raf); window.clearTimeout(timer); };
  }, [meta, bordersReady]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !bordersReady) return;
    setBordersVisible(map, layers.borders);
    // the basemap's present-day boundaries stand in only when historical borders are off
    for (const id of ["boundary-country", "boundary-disputed"]) {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", layers.borders ? "none" : "visible");
    }
  }, [layers.borders, bordersReady]);

  // --- data --------------------------------------------------------------------------------
  const named = useNamedEvents(meta, a, b);
  const { years, grid } = useGed(meta, t, span);
  const areas = useAreas(meta, a, b);
  const namedSet = useMemo(() => buildNamed(named, conflicts, focus, kinds), [named, conflicts, focus, kinds]);
  const gedSet = useMemo(() => buildGed(years, conflicts, focus, kinds), [years, conflicts, focus, kinds]);
  const gridSet = useMemo(() => buildGrid(grid), [grid]);
  const areaPolys = useMemo(() => buildAreas(areas, conflicts, selected), [areas, conflicts, selected]);

  // active top-level conflicts → labels (recomputed only when the set changes)
  const active = useMemo(() => (conflicts ? activeRoots(conflicts, t, kinds).slice(0, 16) : []), [conflicts, t, kinds]);
  const activeKey = active.map((x) => x.i).join(",");
  const labelData = useMemo(() => {
    if (!conflicts) return [];
    // name only conflicts that are prominent enough (the top three of the moment always qualify)
    return active.filter((e, rank) => rank < 3 || conflicts.sig[e.i] >= 0.3).map((e, rank) => {
      // place the name where the conflict is being fought now (its footprint for this period)
      let best: AreaPoly | null = null;
      for (const p of areaPolys) if (p.c === e.i && p.t0 <= t && p.t1 >= t && (!best || p.area > best.area)) best = p;
      const pos = best ? [best.cx, best.cy] : conflicts.hasPoint[e.i] ? [conflicts.lon[e.i], conflicts.lat[e.i]] : null;
      return pos ? { i: e.i, pos, name: conflicts.name[e.i], rank, grp: groupOfKind(conflicts.kind[e.i]) } : null;
    }).filter(Boolean) as { i: number; pos: number[]; name: string; rank: number; grp: number }[];
  }, [activeKey, areaPolys, Math.floor(t * 4)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Screen-space label placement: highest rank first, skip anything that would overlap a placed label.
  const [placed, setPlaced] = useState<Map<number, boolean>>(new Map()); // label → flipped to the left
  const placeLabels = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const boxes: [number, number, number, number][] = [];
    const keep = new Map<number, boolean>();
    const { width, height } = map.getContainer().getBoundingClientRect();
    const hits = (b: [number, number, number, number]) => boxes.some((o) => b[0] < o[2] && b[2] > o[0] && b[1] < o[3] && b[3] > o[1]);
    for (const d of labelData) {
      const p = map.project(d.pos as [number, number]);
      if (p.x < 4 || p.x > width - 4 || p.y < 8 || p.y > height - 8) continue; // anchor must be on screen
      const size = d.rank < 3 ? 14 : d.rank < 8 ? 12.5 : 11.5;
      const w = Math.min(d.name.length, 26) * size * 0.56 + 16, hgt = size * (d.name.length > 26 ? 2.4 : 1.3);
      const right: [number, number, number, number] = [p.x - 6, p.y - hgt / 2, p.x + w, p.y + hgt / 2];
      const left: [number, number, number, number] = [p.x - w, p.y - hgt / 2, p.x + 6, p.y + hgt / 2];
      const fitsR = right[2] <= width - 2 && !hits(right), fitsL = left[0] >= 2 && !hits(left);
      if (!fitsR && !fitsL) continue;
      boxes.push(fitsR ? right : left);
      keep.set(d.i, !fitsR);
    }
    setPlaced((prev) => (prev.size === keep.size && [...keep].every(([k, v]) => prev.get(k) === v) ? prev : keep));
  }, [labelData]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    placeLabels();
    let raf = 0;
    const onMove = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(placeLabels); };
    map.on("move", onMove);
    return () => { map.off("move", onMove); cancelAnimationFrame(raf); };
  }, [placeLabels, styleReady]);

  // --- layers ------------------------------------------------------------------------------
  useEffect(() => {
    const ov = overlay.current;
    if (!ov || !styleReady || !conflicts) return;
    const W = fadeWindow(span);
    const range = [[-1e7, t], [t - W, 1e7]] as [number, number][];
    const soft = [[-1e7, t], [t, 1e7]] as [number, number][];
    const filterExt = new DataFilterExtension({ filterSize: 2 });
    const pointLayer = (id: string, set: PointSet, extra: Record<string, unknown> = {}) => new ScatterplotLayer({
      id, data: { length: set.n, attributes: {
        getPosition: { value: set.positions.length ? set.positions : EMPTY, size: 2 },
        getRadius: { value: set.radii.length ? set.radii : EMPTY, size: 1 },
        getFillColor: { value: set.colors, size: 4, normalized: true },
        getFilterValue: { value: set.filter.length ? set.filter : EMPTY, size: 2 },
      } } as never,
      radiusUnits: "pixels", radiusMinPixels: 1.2, pickable: true, autoHighlight: true, highlightColor: [255, 255, 255, 230],
      extensions: [filterExt], filterRange: range, filterSoftRange: soft, filterTransformSize: true,
      updateTriggers: { getFillColor: [focus, kinds] },
      ...extra,
    } as never);

    const sel = selected, hov = hoverConflict;
    const layersOut = [];
    // areas only for the conflicts named on the map and the selected one (otherwise they pile up)
    const shown = new Set(labelData.map((d) => d.i));
    const inFocus = (ci: number) => focus != null && focus[ci] === 1;
    // a replay is about borders and battles: its war's areas only, softly
    const polys = areaPolys.filter((p) => (replaying ? inFocus(p.c) : shown.has(p.c) || inFocus(p.c) || (hov >= 0 && p.c === hov)));
    if (layers.areas && polys.length) {
      const fillOf = (p: AreaPoly) => {
        const c = GROUP_RGB[groupOfKind(conflicts.kind[p.c])];
        const on = inFocus(p.c), h = p.c === hov;
        const alpha = on ? (replaying ? 14 : 80) : h ? 64 : sel >= 0 ? 16 : p.fc ? 22 : 44;
        return [c[0], c[1], c[2], alpha];
      };
      layersOut.push(new SolidPolygonLayer<AreaPoly>({
        id: "areas", data: polys, getPolygon: (p: AreaPoly) => p.polygon as never, positionFormat: "XY",
        getFillColor: fillOf as never, pickable: true,
        extensions: [new DataFilterExtension({ filterSize: 2 })], getFilterValue: (p: AreaPoly) => [p.t0, p.t1],
        filterRange: [[-1e7, t], [t, 1e7]], updateTriggers: { getFillColor: [focus, hov, replaying] },
      } as never));
      layersOut.push(new PathLayer<AreaPoly>({
        id: "areas-outline", data: polys, getPath: (p: AreaPoly) => p.polygon as never, positionFormat: "XY",
        getColor: (p: AreaPoly) => {
          const c = GROUP_RGB[groupOfKind(conflicts.kind[p.c])];
          return [c[0], c[1], c[2], replaying ? 70 : inFocus(p.c) ? 235 : p.fc ? 70 : 150];
        },
        getWidth: (p: AreaPoly) => (replaying ? 1 : inFocus(p.c) ? 1.8 : 0.9), widthUnits: "pixels",
        extensions: [new DataFilterExtension({ filterSize: 2 })], getFilterValue: (p: AreaPoly) => [p.t0, p.t1],
        filterRange: [[-1e7, t], [t, 1e7]], updateTriggers: { getColor: [focus, replaying], getWidth: [focus, replaying] }, _pathType: "loop",
      } as never));
    }
    if (layers.events) {
      if (span > DETAIL_SPAN && gridSet.n) {
        const Wg = Math.max(W, 1);
        layersOut.push(pointLayer("ged-grid", gridSet, {
          filterRange: [[-1e7, t], [t - Wg, 1e7]], filterSoftRange: [[-1e7, t], [t, 1e7]], pickable: false, autoHighlight: false,
          parameters: { blendColorSrcFactor: "src-alpha", blendColorDstFactor: "one", blendAlphaSrcFactor: "one", blendAlphaDstFactor: "one" },
        }));
      } else if (gedSet.n) {
        layersOut.push(pointLayer("ged", gedSet, {
          parameters: { blendColorSrcFactor: "src-alpha", blendColorDstFactor: "one", blendAlphaSrcFactor: "one", blendAlphaDstFactor: "one" },
        }));
      }
      if (namedSet.n) {
        layersOut.push(pointLayer("named", namedSet, {
          stroked: true, getLineColor: [11, 16, 22, 220], lineWidthUnits: "pixels", getLineWidth: 1,
        }));
      }
    }
    if (event) {
      layersOut.push(new ScatterplotLayer({
        id: "picked", data: [event], getPosition: (e: PickedEvent) => [e.lon, e.lat], getRadius: 14, radiusUnits: "pixels",
        stroked: true, filled: false, getLineColor: [255, 255, 255, 240], lineWidthMinPixels: 2,
      }));
    }
    const labelsShown = labelData.filter((d) => placed.has(d.i)).map((d) => ({ ...d, flip: placed.get(d.i)! }));
    if (layers.labels && labelsShown.length) {
      layersOut.push(new ScatterplotLayer({
        id: "label-dots", data: labelsShown, getPosition: (d: { pos: number[] }) => d.pos as [number, number],
        getRadius: 4, radiusUnits: "pixels", getFillColor: (d: { grp: number }) => [...GROUP_RGB[d.grp], 255] as never,
        stroked: true, getLineColor: [11, 16, 22, 255], lineWidthMinPixels: 1.5,
      } as never));
      layersOut.push(new TextLayer({
        id: "labels", data: labelsShown, getPosition: (d: { pos: number[] }) => d.pos as [number, number],
        getText: (d: { name: string }) => d.name, getSize: (d: { rank: number }) => (d.rank < 3 ? 14 : d.rank < 8 ? 12.5 : 11.5),
        getColor: [234, 237, 241, 255], fontFamily: "'Inter Variable', system-ui, sans-serif", fontWeight: 600,
        characterSet: "auto", outlineWidth: 3, outlineColor: [11, 16, 22, 230], fontSettings: { sdf: true, buffer: 6 },
        getTextAnchor: (d: { flip: boolean }) => (d.flip ? "end" : "start"), getAlignmentBaseline: "center",
        getPixelOffset: (d: { flip: boolean }) => (d.flip ? [-9, 0] : [9, 0]), maxWidth: 14, wordBreak: "break-word",
        pickable: true, updateTriggers: { getTextAnchor: [placed], getPixelOffset: [placed] },
      } as never));
    }
    ov.setProps({
      layers: layersOut,
      onHover: (info) => onHover(info as never),
      onClick: (info) => onClick(info as never),
      getCursor: ({ isHovering }) => (isHovering ? "pointer" : "grab"),
    });
  }, [styleReady, conflicts, t, span, namedSet, gedSet, gridSet, areaPolys, labelData, placed, layers, focus, hoverConflict, event, kinds, replaying]);

  // --- picking ----------------------------------------------------------------------------
  function describe(layerId: string, index: number, object: unknown): PickedEvent | null {
    const c = useApp.getState().conflicts;
    if (!c) return null;
    if (layerId === "named") {
      const r = namedSet.lookup(index), ch = r.ref as NamedEvents, j = r.k;
      return {
        src: "named", name: ch.name[j], date: eventDateLabel(ch.t0[j], ch.t1[j], ch.pr[j], !!ch.jl[j]),
        deaths: ch.d[j] || null, kind: ch.k[j], conflict: ch.c[j], lon: ch.lon[j], lat: ch.lat[j], id: ch.id[j],
        url: ch.u?.[j] || undefined,
      };
    }
    if (layerId === "ged") {
      const r = gedSet.lookup(index), y = r.ref as GedYear, j = r.k;
      return {
        src: "ged", name: y.dyads[y.dy[j]] || "Organised violence", date: eventDateLabel(y.t0[j], y.t1[j], 11, false),
        place: y.places[y.p[j]], deaths: y.b[j], kind: 5 + y.ty[j], conflict: y.c[j], lon: y.lon[j], lat: y.lat[j],
        provisional: !!(y.pv && y.pv[j]), precise: y.gp[j],
      };
    }
    void object;
    return null;
  }
  function onHover(info: { layer?: { id: string }; index: number; x: number; y: number; object?: unknown }) {
    const st = useApp.getState();
    const c = st.conflicts;
    if (!info.layer || info.index < 0 || !c) { if (st.hover) st.setHover(null); if (st.hoverConflict >= 0 && !info.layer) st.setHoverConflict(-1); return; }
    const id = info.layer.id;
    if (id === "areas" || id === "labels") {
      const ci = id === "areas" ? (info.object as AreaPoly).c : (info.object as { i: number }).i;
      st.setHoverConflict(ci);
      st.setHover({ x: info.x, y: info.y, title: c.name[ci], lines: [KIND_LABEL[c.kind[ci]]] });
      return;
    }
    const e = describe(id, info.index, info.object);
    if (!e) return;
    const lines = [e.date];
    if (e.place) lines.push(e.place);
    if (e.deaths) lines.push(`${fmtDeaths(e.deaths)} deaths${e.src === "ged" ? " (UCDP best estimate)" : ""}`);
    if (e.conflict >= 0) lines.push(c.name[c.r[e.conflict] >= 0 ? c.r[e.conflict] : e.conflict]);
    st.setHover({ x: info.x, y: info.y, title: e.name, lines });
  }
  function onClick(info: { layer?: { id: string }; index: number; object?: unknown }) {
    const st = useApp.getState();
    if (!info.layer || info.index < 0) { st.setEvent(null); return; }
    const id = info.layer.id;
    if (id === "areas") { st.select((info.object as AreaPoly).c); return; }
    if (id === "labels") { st.select((info.object as { i: number }).i, { fly: true }); return; }
    const e = describe(id, info.index, info.object);
    if (e) st.setEvent(e);
  }

  return (
    <div className="map-stage" ref={stageRef} data-grade={cinematic ? gradeOf(t) : undefined}>
      <div className="map" ref={el} data-testid="map" />
      <FxLayer mapRef={mapRef} stageRef={stageRef} named={named} ged={years} />
      <CinemaFilm />
    </div>
  );
}

